import { useEffect, useState, type RefObject } from 'react';

const MIN_LINES = 2;
const MAX_LINES = 8;

/**
 * Sizes a composer textarea to its full content so it never scrolls internally,
 * resting at MIN_LINES. Returns the height to put on the wrapping <ScrollArea>,
 * capped at MAX_LINES — Radix's viewport is `h-full`, so the scroll container
 * needs a definite height before it will ever overflow.
 *
 * Heights are a whole number of line boxes plus the textarea's own vertical
 * padding, so a border-box textarea never renders a half-clipped line.
 */
export function useAutoGrowTextarea(
    ref: RefObject<HTMLTextAreaElement | null>,
    value: string
) {
    const [height, setHeight] = useState<number>();

    useEffect(() => {
        const el = ref.current;
        if (!el) return;

        el.style.height = 'auto';

        const styles = getComputedStyle(el);
        const line = parseFloat(styles.lineHeight) || 20;
        const padding =
            parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom);

        const content = Math.max(el.scrollHeight, line * MIN_LINES + padding);
        el.style.height = `${content}px`;
        setHeight(Math.min(content, line * MAX_LINES + padding));

        // The textarea can no longer scroll to reveal the caret, so keep the
        // viewport pinned while the caret sits at the end of the draft.
        const viewport = el.closest<HTMLElement>(
            '[data-radix-scroll-area-viewport]'
        );
        if (viewport && el.selectionStart === el.value.length) {
            viewport.scrollTop = viewport.scrollHeight;
        }
    }, [ref, value]);

    return height;
}
